/**
 * Hostile mobs: zombie, skeleton, creeper, spider. Vanilla stats (normal
 * difficulty): zombie 20hp 3dmg speed 0.23, skeleton 20hp bow, creeper 20hp
 * 30-tick fuse power 3, spider 16hp speed 0.3 2dmg (neutral in bright light).
 */

import { I } from '../game/itemIds';
import { stackOf, type MobType, type Stack } from '../game/types';
import { Mob, type MobCtx } from './mob';
import { applyUndeadVariants, armorDrops, BABY_ZOMBIE_CHANCE, makeBaby } from './overworld2/variants';
import { navHooks } from './nav/hooks';
import { isSpearId, mobSpearDamage, rollMobSpear } from '../game/spear';
import { effectiveLight } from './host';

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

/** Shared targeting: acquire the player by sight, remember them for a while. */
export abstract class Hostile extends Mob {
  protected seeMemory = 0;
  protected hasTarget = false;
  /** Did the last sight check see the player? */
  protected sees = false;

  override onHurtBy(): void { this.retaliate = 200; }

  xpValue(): number { return 5; }

  /** Vanilla Monster.getWalkTargetValue: wander toward the dark. */
  protected override walkValue(x: number, y: number, z: number): number { return 0.5 - effectiveLight(this.host, x, y, z) / 15; }

  /** Update `hasTarget` (follow range `range`, remembered 60 ticks after losing sight). */
  protected acquire(ctx: MobCtx, range: number): boolean {
    const p = ctx.host.player;
    if (!ctx.targetable()) { this.hasTarget = false; this.seeMemory = 0; this.sees = false; return false; }
    const dx = p.x - this.x, dz = p.z - this.z, dy = p.y - this.y;
    const d2 = dx * dx + dz * dz + dy * dy;
    range *= ctx.host.detectScale?.() ?? 1; // invisible players are only noticed from up close
    if (d2 > range * range) { this.hasTarget = false; this.seeMemory = 0; this.sees = false; return false; }
    if (this.phase(ctx, 5)) {
      this.sees = ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z)
        || ctx.los(this.x, this.eyeY, this.z, p.x, p.y + 0.5, p.z);
      if (this.sees) this.seeMemory = 60;
    }
    if (this.seeMemory > 0) this.seeMemory--;
    if (this.retaliate > 0) this.seeMemory = Math.max(this.seeMemory, 1);
    this.hasTarget = this.seeMemory > 0;
    return this.hasTarget;
  }

  /** Walk toward the player (skipping steps that would end in lava/fire/deep drops). */
  protected chase(ctx: MobCtx, mod: number): void {
    const p = ctx.host.player;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    // path around walls / over steps; plain steering when there is no route
    this.steerTo(ctx, p.x, p.y, p.z, mod, 6);
  }

  /** Run from whatever this mob fears (creeper -> cat, skeleton -> wolf); true while fleeing. */
  protected flee(ctx: MobCtx): boolean {
    const f = navHooks.scaredOf?.(this);
    if (!f || !f.alive || (f.x - this.x) ** 2 + (f.z - this.z) ** 2 > 36) return false;
    return this.navAway(ctx, f.x, f.z, 1.2, 3, true);
  }
}

export class Zombie extends Hostile {
  private attackCd = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number, type: MobType = 'zombie') {
    super(id, type, x, y, z, { width: 0.6, height: 1.95, eye: 1.74, health: 20, speed: 0.23 }, rnd);
    applyUndeadVariants(this, rnd, true);
    if (!this.heldItem && (type === 'zombie' || type === 'husk')) this.heldItem = rollMobSpear(type, rnd); // game/spear.ts
    if (type !== 'zombie_villager' && rnd() < BABY_ZOMBIE_CHANCE) makeBaby(this);
  }

  /** Hard difficulty: zombies hack through wooden doors. */
  protected override doorMode(ctx: MobCtx): 0 | 1 | 2 { return ctx.host.difficulty?.() === 'hard' ? 2 : 0; }

  protected ai(ctx: MobCtx): void {
    this.sunBurn(ctx);
    if (this.attackCd > 0) this.attackCd--;
    if (this.acquire(ctx, 40)) {
      this.aggressive = true;
      this.chase(ctx, 1);
      if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
        this.attackCd = 20;
        this.swing();
        // a spear carrier hits with its charge: closing speed x tier multiplier (game/spear.ts)
        ctx.hurtPlayer(this, isSpearId(this.heldItem) ? mobSpearDamage(this.heldItem, Math.hypot(this.vx, this.vz)) : 3 + this.meleeBonus, 0.4);
      }
    } else if (!this.seekShade(ctx, 1, 4)) {
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
    }
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    const flesh = roll(rnd, 0, 2);
    if (flesh > 0) out.push(stackOf(I.ROTTEN_FLESH, flesh));
    if (byPlayer && rnd() < 1 / 40) out.push(stackOf(I.IRON_INGOT, 1));
    if (byPlayer) out.push(...armorDrops(this.armorPieces, rnd));
    return out;
  }
}

export class Skeleton extends Hostile {
  private shootCd = 40;
  private drawTicks = 0;
  private seeTime = 0;
  private strafeTime = 0;
  private strafeBack = false;
  private strafeLeft = false;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number, type: MobType = 'skeleton') {
    super(id, type, x, y, z, { width: 0.6, height: 1.99, eye: 1.74, health: 20, speed: 0.25 }, rnd);
    this.heldItem = I.BOW;
    applyUndeadVariants(this, rnd, false);
  }

  protected ai(ctx: MobCtx): void {
    this.sunBurn(ctx);
    if (!this.acquire(ctx, 20)) {
      this.drawTicks = 0; this.seeTime = 0;
      if (this.shootCd < 20) this.shootCd++;
      if (this.flee(ctx) || this.seekShade(ctx, 1, 4)) return;
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
      return;
    }
    if (this.flee(ctx)) return;
    const p = ctx.host.player;
    const dx = p.x - this.x, dz = p.z - this.z;
    const d2 = dx * dx + dz * dz;
    const radius2 = 15 * 15;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);

    this.seeTime = this.sees ? this.seeTime + 1 : Math.max(0, this.seeTime - 1);

    if (d2 <= radius2 && this.seeTime >= 20) {
      // vanilla RangedBowAttackGoal: hold position and strafe
      this.strafeTime++;
      if (this.strafeTime >= 20) {
        if (ctx.host.rnd() < 0.3) this.strafeLeft = !this.strafeLeft;
        if (ctx.host.rnd() < 0.3) this.strafeBack = !this.strafeBack;
        this.strafeTime = 0;
      }
      if (d2 > radius2 * 0.75) this.strafeBack = false;
      else if (d2 < radius2 * 0.25) this.strafeBack = true;
      // movement relative to the player: forward = toward, side = perpendicular
      const l = Math.sqrt(d2) || 1;
      const fx = dx / l, fz = dz / l;
      const fw = this.strafeBack ? -0.5 : 0.5;
      const sd = this.strafeLeft ? 0.5 : -0.5;
      const mx = fx * fw + -fz * sd, mz = fz * fw + fx * sd;
      if (this.safeAhead(mx, mz, 4, false)) this.setMove(mx, mz, 0.7);
    } else {
      this.chase(ctx, 1);
    }

    if (this.sees && d2 <= radius2) {
      this.aggressive = true;
      if (this.shootCd > 0) this.shootCd--;
      if (this.drawTicks < 20) this.drawTicks++;
      if (this.drawTicks >= 20 && this.shootCd <= 0) this.shoot(ctx);
    } else {
      this.drawTicks = 0;
      if (this.shootCd < 20) this.shootCd++;
    }
  }

  private shoot(ctx: MobCtx): void {
    const p = ctx.host.player;
    const sx = this.x, sy = this.eyeY - 0.1, sz = this.z;
    const dx = p.x - sx;
    const dy = p.y + p.height * 0.333 - sy;
    const dz = p.z - sz;
    const h = Math.hypot(dx, dz);
    // vanilla: aim a little high to account for gravity; normal-difficulty spread
    ctx.shootArrow(sx, sy, sz, dx, dy + h * 0.2, dz, 1.6, 2, false, 6, this.id);
    this.drawTicks = 0;
    this.shootCd = 40;
    this.swing();
  }

  loot(rnd: () => number, byPlayer = false): Stack[] {
    const out: Stack[] = [];
    const bones = roll(rnd, 0, 2), arrows = roll(rnd, 0, 2);
    if (bones > 0) out.push(stackOf(I.BONE, bones));
    if (arrows > 0) out.push(stackOf(I.ARROW, arrows));
    if (byPlayer) out.push(...armorDrops(this.armorPieces, rnd));
    return out;
  }
}

export class Creeper extends Hostile {
  private swellTicks = 0;
  private swellDir = -1;
  /** Lit with flint and steel: the fuse runs to the end whatever the player does. */
  ignited = false;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'creeper', x, y, z, { width: 0.6, height: 1.7, eye: 1.445, health: 20, speed: 0.25 }, rnd);
  }

  protected ai(ctx: MobCtx): void {
    const has = this.acquire(ctx, 16);
    const p = ctx.host.player;
    let d2 = Infinity;
    if (has) {
      const dx = p.x - this.x, dy = p.y - this.y, dz = p.z - this.z;
      d2 = dx * dx + dy * dy + dz * dz;
    }
    // vanilla SwellGoal: start within 3 blocks, give up beyond 7 or when out of sight
    if (!has || d2 > 49 || !this.sees) this.swellDir = -1;
    else if (d2 < 9) this.swellDir = 1;
    else if (this.swellDir > 0 && d2 >= 9 && d2 <= 49 && this.sees) this.swellDir = 1;

    if (this.ignited) this.swellDir = 1;
    this.swellTicks = Math.max(0, Math.min(30, this.swellTicks + this.swellDir));
    this.swell = this.swellTicks / 30;
    this.aggressive = this.swellDir > 0;
    if (this.swellTicks >= 30) { ctx.explodeCreeper(this); return; }

    if (this.flee(ctx)) return;
    if (has) {
      this.lookAt(p.x, p.eyeY, p.z);
      if (this.swellDir > 0) this.faceToward(p.x, p.z); // stands still while the fuse burns
      else this.chase(ctx, 1);
    } else {
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
    }
  }

  loot(rnd: () => number): Stack[] {
    const n = roll(rnd, 0, 2);
    return n > 0 ? [stackOf(I.GUNPOWDER, n)] : [];
  }
}

export class Spider extends Hostile {
  private attackCd = 0;
  /** Hit effect hook (cave spider poison): called right after a melee hit lands. */
  protected onMeleeHit(_ctx: MobCtx): void {}
  /** Melee damage (vanilla normal difficulty). */
  protected meleeDamage = 2;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number, type: MobType = 'spider',
    spec: { width: number; height: number; eye: number; health: number; speed: number } = { width: 1.4, height: 0.9, eye: 0.65, health: 16, speed: 0.3 }) {
    super(id, type, x, y, z, spec, rnd);
    this.climbs = true;
    this.noFallDamage = false;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    // vanilla: calm in bright light unless provoked
    // sky light is raw (15 at night too): scale it by the time of day like vanilla's skyDarken
    const bx = Math.floor(this.x), by = Math.floor(this.y + this.height * 0.5), bz = Math.floor(this.z);
    const bright = Math.max(ctx.host.world.getSky(bx, by, bz) * ctx.host.sunLevel(), ctx.host.world.getBlockLight(bx, by, bz)) / 15 >= 0.8;
    let has = this.acquire(ctx, 16);
    if (has && bright && this.retaliate <= 0) { has = false; this.hasTarget = false; this.seeMemory = 0; }
    if (!has) {
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
      return;
    }
    this.aggressive = true;
    const p = ctx.host.player;
    this.chase(ctx, 1);
    const dx = p.x - this.x, dz = p.z - this.z;
    const d = Math.hypot(dx, dz);
    // vanilla leap: pounce from 2-4 blocks away
    if (this.onGround && d >= 2 && d <= 4 && Math.abs(p.y - this.y) < 1.5 && ctx.host.rnd() < 0.1) {
      this.vx = (dx / d) * 0.4 + this.vx * 0.2;
      this.vz = (dz / d) * 0.4 + this.vz * 0.2;
      this.vy = 0.4;
    }
    if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
      this.attackCd = 20;
      this.swing();
      ctx.hurtPlayer(this, this.meleeDamage, 0.4);
      this.onMeleeHit(ctx);
    }
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    const s = roll(rnd, 0, 2);
    if (s > 0) out.push(stackOf(I.STRING, s));
    if (byPlayer && rnd() < 1 / 3) out.push(stackOf(I.SPIDER_EYE, 1));
    return out;
  }
}
